import type {
  AddProcedureMaterial,
  AdjustInventory,
  CreateInventoryItem,
  EditProcedureMaterial,
  InventoryItem,
  InventoryMovement,
  PrepList,
  ProcedureMaterial,
  UpdateInventoryItem,
} from "@dentacore/shared-types";

import { api } from "./client";
import { announceStockMoved } from "./stock-moved";

/**
 * The active branch's stock — STORY-109.
 *
 * **All of it is branch-scoped**, so every call here carries the active-branch
 * header and a screen must wait for the branch before firing one (see
 * `useActiveBranch`, and Gotchas → *a branch-scoped fetch must wait*).
 *
 * **The role split is decision TP17 and it is not symmetrical.** Listing,
 * adjusting and opening are open to every signed-in user; creating, editing and
 * retiring are admin-only and the server refuses anyone else with a 403. That
 * is deliberate: the person who opens a tube is the person at the chair, and
 * admin-only recording would leave every bulk count permanently wrong.
 */

/**
 * `includeRetired` separates the two callers: Settings wants everything so a
 * retired item can be brought back, everything else wants what is current.
 */
export function listInventory(
  includeRetired = false,
  signal?: AbortSignal,
): Promise<InventoryItem[]> {
  return api<InventoryItem[]>(`/inventory${includeRetired ? "?includeRetired=true" : ""}`, {
    branchScoped: true,
    signal,
  });
}

/** **Admin only** — the server refuses anyone else before this resolves. */
export function createInventoryItem(body: CreateInventoryItem): Promise<InventoryItem> {
  return api<InventoryItem>("/inventory", { method: "POST", body, branchScoped: true });
}

/**
 * Edit or retire. **Admin only.**
 *
 * The count cannot be sent here — it is not on the type, and the server refuses
 * the property rather than ignoring it. Correcting a count is `adjustInventory`.
 */
export function updateInventoryItem(
  itemId: string,
  body: UpdateInventoryItem,
): Promise<InventoryItem> {
  return api<InventoryItem>(`/inventory/${itemId}`, { method: "PATCH", body, branchScoped: true });
}

/**
 * Correct a count, with a reason. **Any signed-in user** (TP17).
 *
 * The result may be negative and is not refused — stock refuses nothing.
 */
export async function adjustInventory(
  itemId: string,
  body: AdjustInventory,
): Promise<InventoryItem> {
  const item = await api<InventoryItem>(`/inventory/${itemId}/adjust`, {
    method: "POST",
    body,
    branchScoped: true,
  });
  announceStockMoved();
  return item;
}

/**
 * Mark a bulk unit opened — one whole unit, never a fraction of a pack.
 * **Any signed-in user** (TP17); this is the chairside action the split exists
 * for. Refused on anything that is not a bulk item.
 */
/** Mark or unmark an item to buy (WB-D1). Placing an order for it clears it. */
export function setWantToBuy(itemId: string, want: boolean): Promise<InventoryItem> {
  return api<InventoryItem>(`/inventory/${itemId}/want-to-buy`, {
    method: "POST",
    body: { want },
    branchScoped: true,
  });
}

export async function openInventoryUnit(itemId: string): Promise<InventoryItem> {
  const item = await api<InventoryItem>(`/inventory/${itemId}/open`, {
    method: "POST",
    branchScoped: true,
  });
  announceStockMoved();
  return item;
}

/**
 * A procedure's bill of materials — STORY-110b.
 *
 * **Every write returns the whole list**, not the row it changed: a bill is
 * short and always read as a set, so one response keeps the screen and the
 * server from disagreeing after an edit. Callers replace their state with it
 * rather than re-fetching.
 *
 * Reading is open to every signed-in user (the prep list needs it); adding,
 * editing and removing are admin-only and the server refuses anyone else.
 */
export function listProcedureMaterials(
  procedureCodeId: string,
  signal?: AbortSignal,
): Promise<ProcedureMaterial[]> {
  return api<ProcedureMaterial[]>(`/procedure-codes/${procedureCodeId}/materials`, {
    branchScoped: true,
    signal,
  });
}

export function addProcedureMaterial(
  procedureCodeId: string,
  body: AddProcedureMaterial,
): Promise<ProcedureMaterial[]> {
  return api<ProcedureMaterial[]>(`/procedure-codes/${procedureCodeId}/materials`, {
    method: "POST",
    body,
    branchScoped: true,
  });
}

export function editProcedureMaterial(
  procedureCodeId: string,
  materialId: string,
  body: EditProcedureMaterial,
): Promise<ProcedureMaterial[]> {
  return api<ProcedureMaterial[]>(`/procedure-codes/${procedureCodeId}/materials/${materialId}`, {
    method: "PATCH",
    body,
    branchScoped: true,
  });
}

export function removeProcedureMaterial(
  procedureCodeId: string,
  materialId: string,
): Promise<ProcedureMaterial[]> {
  return api<ProcedureMaterial[]>(`/procedure-codes/${procedureCodeId}/materials/${materialId}`, {
    method: "DELETE",
    branchScoped: true,
  });
}

/**
 * What to put on the tray for one booking — STORY-110c, and the answer to the
 * sentence this whole phase came from.
 *
 * **Open to every signed-in user**, because the person setting a tray is not an
 * admin. A booking with no procedure comes back with an empty list rather than
 * an error.
 */
export function getPrepList(appointmentId: string, signal?: AbortSignal): Promise<PrepList> {
  return api<PrepList>(`/appointments/${appointmentId}/prep-list`, {
    branchScoped: true,
    signal,
  });
}

/** One item's ledger, newest first. A retired item keeps its history. */
export function listInventoryMovements(
  itemId: string,
  signal?: AbortSignal,
): Promise<InventoryMovement[]> {
  return api<InventoryMovement[]>(`/inventory/${itemId}/movements`, {
    branchScoped: true,
    signal,
  });
}
